import Vue from 'vue';
import VueRouter from 'vue-router';
import {isEmpty} from '@/utils';
import store from '@/store';
import {getAccessToken} from '@/utils/access-token';
import routeAnimation from './route-animation'
import routeRecordHistory from './route-record-history'

import Routes from './routes';
import ServicesRoutes from './services-routes';
// import PharmacyOrdersRoutes from './pharmacy-orders-routes';
import MedicineOrderDetailsRoute from './medicine-order-details-route';

import {APPOINTMENT_DETAIL_TYPE_PROP, APPOINTMENT_TYPES} from '@/constants';

Vue.use(VueRouter);

const routes = [
  {
    path: '/login',
    name: Routes.LOGIN_ROUTE,
    beforeEnter: (to, from, next) => {
      if (!isEmpty(getAccessToken())) {
        return next(from);
      }

      next();
    },
    component: () => import('@/views/Auth'),
    props: true,
  },
  {
    path: '',
    redirect: Routes.HOME_ROUTE,
    component: () => import('@/views/Main'),
    beforeEnter: (to, from, next) => {
      if (isEmpty(getAccessToken())) {
        next({name: Routes.LOGIN_ROUTE});
      }

      next();
    },
    children: [
      {
        path: '/home',
        name: Routes.HOME_ROUTE,
        component: () => import('@/views/Home'),
      },
      {
        path: '/account',
        name: Routes.ACCOUNT_ROUTE,
        component: () => import('@/views/account-settings'),
      },
      {
        path: '/doctors',
        name: Routes.DOCTORS_ROUTE,
        component: () => import('@/views/doctors/doctors-page'),
        children: [
          {
            path: 'list',
            name: Routes.DOCTORS_ROUTE_LIST,
            component: () => import('@/views/doctors/doctors-list'),
            meta: {
              root: Routes.DOCTORS_ROUTE,
            },
          },
          {
            path: 'appointments',
            redirect: Routes.APPOINTMENTS_ROUTE,
            component: () => import('@/views/doctors/appointments-page'),
            children: [
              {
                path: '',
                name: Routes.APPOINTMENTS_ROUTE,
                component: () => import('@/views/doctors/appointments-list'),
                meta: {
                  root: Routes.DOCTORS_ROUTE,
                },
              },
              {
                path: `approved/:${Routes.APPOINTMENT_DETAIL_PARAM}`,
                name: Routes.APPOINTMENT_APPROVED_ROUTE,
                component: () => import('@/views/doctors/appointment-details'),
                meta: {
                  root: Routes.DOCTORS_ROUTE,
                },
              },
              {
                path: `pending/:${Routes.APPOINTMENT_DETAIL_PARAM}`,
                name: Routes.APPOINTMENT_PENDING_ROUTE,
                component: () => import('@/views/doctors/appointment-details'),
                props: {[APPOINTMENT_DETAIL_TYPE_PROP]: APPOINTMENT_TYPES.PENDING},
                meta: {
                  root: Routes.DOCTORS_ROUTE,
                },
              },
              {
                path: `denied/:${Routes.APPOINTMENT_DETAIL_PARAM}`,
                name: Routes.APPOINTMENT_DENIED_ROUTE,
                component: () => import('@/views/doctors/appointment-details'),
                props: {[APPOINTMENT_DETAIL_TYPE_PROP]: APPOINTMENT_TYPES.DENIED},
                meta: {
                  root: Routes.DOCTORS_ROUTE,
                },
              },
              {
                path: `requests/:${Routes.APPOINTMENT_DETAIL_PARAM}`,
                name: Routes.APPOINTMENT_NEW_REQUESTS_ROUTE,
                component: () => import('@/views/doctors/appointment-details'),
                props: {[APPOINTMENT_DETAIL_TYPE_PROP]: APPOINTMENT_TYPES.NEW_REQUEST},
                meta: {
                  root: Routes.DOCTORS_ROUTE,
                },
              },
            ],
          },
          {
            path: 'doctor-search',
            name: Routes.DOCTOR_SEARCH_ROUTE,
            component: () => import('@/views/doctors/doctor-search'),
            meta: {
              root: Routes.DOCTORS_ROUTE,
            },
          },
          {
            path: `:${Routes.DOCTOR_ROUTE_PARAM}`,
            name: Routes.DOCTOR_ROUTE,
            component: () => import('@/views/doctors/doctor-page'),
            meta: {
              root: Routes.DOCTORS_ROUTE,
            },
          },
        ],
      },
      {
        path: '/messages',
        component: () => import('@/views/messages/root-messages'),
        name: Routes.MESSAGES_ROUTE,
        redirect: {
          name: Routes.MESSAGES_ROUTE_ALL
        },
        children: [
          {
            path: 'all',
            name: Routes.MESSAGES_ROUTE_ALL,
            component: () => import('@/views/messages/messages-list'),
            meta: {
              root: Routes.MESSAGES_ROUTE,
            }
          },
          {
            path: 'doctors',
            name: Routes.DOCTOR_MESSAGES_ROUTE,
            component: () => import('@/views/messages/doctor-messages/root-doctor-messages'),
            meta: {
              root: Routes.MESSAGES_ROUTE,
            }
          },
          {
            path: 'services',
            name: Routes.SERVICE_MESSAGES_ROUTE,
            component: () => import('@/views/messages/service-messages/root-service-messages'),
            meta: {
              root: Routes.MESSAGES_ROUTE,
            }
          },
          {
            path: `:${Routes.MESSAGE_DOCTOR_PARAM}/:${Routes.MESSAGE_PATIENT_ID_PARAM}`,
            name: Routes.CHAT_ROUTE,
            component: () => import('@/views/messages/chat/root-chat'),
            props: true,
          },
          {
            path: `:${Routes.ORDER_CHAT_ROUTE_PARAM}`,
            name: Routes.ORDER_CHAT_ROUTE,
            component: () => import('@/views/messages/chat/order-chat'),
          },
        ],
      },
      {
        path: `/messages/:${Routes.MESSAGE_DOCTOR_PARAM}/:${Routes.MESSAGE_PATIENT_ID_PARAM}/preview`,
        name: Routes.CHAT_PREVIEW_LAB_REQUEST_FILE_ROUTE,
        components: {
          overlay: () => import('@/components/preview-file-lab-request')
        },
        props: {
          overlay: true
        },
        meta: {
          level: 1,
          isOverlay: true,
          root: Routes.MESSAGES_ROUTE
        }
      },
      {
        path: '/files',
        name: Routes.FILES_ROUTE,
        component: () => import('@/views/files/root-files'),
        redirect: {
          name: Routes.FILES_ALL_FILES_ROUTE
        },
        children: [
          {
            path: 'all',
            name: Routes.FILES_ALL_FILES_ROUTE,
            component: () => import('@/views/files/views/view-files-all'),
            meta: {
              root: Routes.FILES_ROUTE,
            },
          },
          {
            path: 'prescriptions',
            name: Routes.FILES_PRESCRIPTIONS_ROUTE,
            component: () => import('@/views/files/views/view-files-prescriptions'),
            meta: {
              root: Routes.FILES_ROUTE,
            },
          },
          {
            path: 'lab-tests',
            name: Routes.FILES_LABTESTS_ROUTE,
            component: () => import('@/views/files/views/view-files-labtests'),
            meta: {
              root: Routes.FILES_ROUTE,
            },
          },
          {
            path: 'other-notes',
            name: Routes.FILES_OTHER_NOTES_ROUTE,
            component: () => import('@/views/files/views/view-files-other-notes'),
            meta: {
              root: Routes.FILES_ROUTE,
            },
          },
        ]
      },
      {
        path: '/files/preview-file',
        name: Routes.PREVIEW_FILE,
        components: {
          overlay: () => import(/* webpackChunkName: "clinics-chunk" */ '@/components/preview-file')
        },
        props: {
          overlay: true
        },
        meta: {
          level: 1,
          root: Routes.FILES_ROUTE,
          isOverlay: true
        }
      },
      {
        path: '/files/preview-file-lab-request',
        name: Routes.PREVIEW_FILE_LAB_REQUEST,
        components: {
          overlay: () => import(/* webpackChunkName: "clinics-chunk" */ '@/components/preview-file-lab-request')
        },
        props: {
          overlay: true
        },
        meta: {
          level: 1,
          root: Routes.FILES_ROUTE,
          isOverlay: true
        }
      },
      {
        path: `/book/:${Routes.BOOKING_ROUTE_PARAM}`,
        name: Routes.BOOKING_ROUTE,
        component: () => import('@/views/Booking.vue'),
      },
      {
        path: '/services',
        name: Routes.SERVICES_ROUTE,
        props: true,
        component: () => import ('@/views/services/root-services'),
        redirect: {name: Routes.SERVICES_ONGOING_ROUTE},
        children: ServicesRoutes
      },
      {
        path: `/services/pharmacy-orders/fulfillment-mode/:${Routes.PHARMACY_FULFILLMENT_MODE_PARAM}`,
        name: Routes.PHARMACY_FULFILLMENT_MODE,
        components: {
          overlay: () => import (/* webpackChunkName: "clinics-chunk" */'@/views/pharmacy-orders/views/fulfillment-mode-selection')
        },
        meta: {
          level: 3,
          isOverlay: true
        },
      },
      {
        path: `/services/pharmacy-watsons-orders/:${Routes.PHARMACY_FULFILLMENT_MODE_PARAM}`,
        name: Routes.PHARMACY_WATSONS_ORDERS,
        components: {
          overlay: () => import(/* webpackChunkName: "clinics-chunk" */ '@/views/pharmacy-orders/root-pharmacy-orders')
        },
        meta: {
          isOverlay: true
        },
      },
      // PharmacyOrdersRoutes,
      {
        path: '/services/pharmacy-orders/add-prescriptions',
        name: Routes.PHARMACY_ORDERS_ADD_PRESCRIPTION,
        components: {
          overlay: () => import(/* webpackChunkName: "clinics-chunk" */ '@/components/add-prescription')
        },
        meta: {
          level: 1,
          isOverlay: true,
          root: Routes.SERVICES_ROUTE
        }
      },
      {
        path: '/services/pharmacy-orders/preview-file',
        name: Routes.PHARMACY_PREVIEW_FILE,
        components: {
          overlay: () => import(/* webpackChunkName: "clinics-chunk" */ '@/components/preview-file')
        },
        props: {
          overlay: true
        },
        meta: {
          level: 2,
          isOverlay: true,
          root: Routes.SERVICES_ROUTE
        }
      },
      // {
      //   path: '/services/lab-request',
      //   name: Routes.LAB_REQUESTS,
      //   components: {
      //     overlay: () => import(/* webpackChunkName: "clinics-chunk" */ '@/views/services/lab-request/root-lab-request')
      //   },
      //   meta: {
      //     level: 1,
      //     isOverlay: true,
      //     root: Routes.SERVICES_ROUTE
      //   }
      // },
      {
        path: '/services/lab-request/preview-file',
        name: Routes.LAB_REQUESTS_PREVIEW_FILE,
        components: {
          overlay: () => import(/* webpackChunkName: "clinics-chunk" */ '@/components/preview-file-lab-request')
        },
        props: {
          overlay: true
        },
        meta: {
          level: 2,
          isOverlay: true,
          root: Routes.SERVICES_ROUTE
        }
      },
      MedicineOrderDetailsRoute,
      {
        path: `/medicine-order/lab-request/:${Routes.MEDICINE_ORDER_ID_PARAM}`,
        name: Routes.MEDICINE_ORDER_LAB_REQUESTS,
        components: {
          overlay: () => import(/* webpackChunkName: "medicine-order-details-chunk" */ '@/views/common/medicine-order-details/root-medicine-order-details')
        },
        props: {
          overlay: true
        },
        meta: {
          level: 2,
          isOverlay: true,
          root: Routes.SERVICES_ROUTE
        },
      },
      {
        path: '/medicine-order/preview-file',
        name: Routes.MEDICINE_ORDER_PREVIEW_FILE,
        components: {
          overlay: () => import(/* webpackChunkName: "clinics-chunk" */ '@/components/preview-file')
        },
        props: {
          overlay: true
        },
        meta: {
          level: 2,
          isOverlay: true,
          root: Routes.SERVICES_ROUTE
        }
      },
      {
        path: '/program',
        name: Routes.PROGRAM_ROOT,
        component: () => import('@/views/program/root-program'),
        beforeEnter: (to, from, next) => {
          if (store.state.program.coupon) {
            next();
          } else {
            next({name: Routes.ERROR_ROUTE});
          }
        }
      },
      {
        path: '/ondemand/:bookingID',
        name: Routes.ONDEMAND_ROOT,
        component: () => import('@/views/ondemand/root-ondemand'),
        props: true,
      },
    ],
  },
  {
    path: `/invitecode`,
    name: Routes.INVITE_CODE_ROUTE,
    component: () => import('@/views/InviteCode'),
  },
  {
    path: `/clinicqr/:${Routes.CLINIC_QR_DOCTOR_PARAM}`,
    name: Routes.CLINIC_QR_ROUTE,
    component: () => import('@/views/clinic-qr-landing'),
    props: true,
  },
  {
    path: '/error',
    name: Routes.ERROR_ROUTE,
    component: () => import('@/views/error-page'),
  },
  {
    path: '*',
    redirect: Routes.ERROR_ROUTE,
  },
];


const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach((to, from, next) => {
  routeAnimation(to, from);
  next();
});

router.afterEach((to, from) => {
  routeRecordHistory(to, from);
});

export default router;
